// components/shared/reviews/ReviewRow.tsx
import { Review } from "../../../types/features/Review";
import TableRow from "../../ui/TableRow";
import { Star } from "lucide-react";

interface ReviewRowProps {
  review: Review;
  onAction: (review: Review) => void;
  onSelect?: (review: Review, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

export default function ReviewRow({
  review,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false,
}: ReviewRowProps) {
  const getPlatformBadge = () => {
    if (review.platform === "play_store") {
      return { label: "Play Store", className: "bg-green-100 text-green-700" };
    }
    return { label: "App Store", className: "bg-blue-100 text-blue-700" };
  };

  const platformBadge = getPlatformBadge();
  const stars = Array(5).fill(0).fill(1, 0, review.rating);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <TableRow
      item={review}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          {review.user_image ? (
            <img src={review.user_image} alt={review.user_name} className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 text-sm font-medium">{review.user_name.charAt(0).toUpperCase()}</span>
            </div>
          )}
          <div>
            <span className="font-medium text-gray-800 block">{review.user_name}</span>
            <div className="flex items-center gap-1 mt-0.5">
              {stars.map((_, i) => (
                <Star
                  key={i}
                  size={12}
                  className={i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}
                />
              ))}
            </div>
          </div>
        </div>
      </td>

      <td className="py-3 px-4">
        <p className="text-gray-600 text-sm line-clamp-2 max-w-xs">{review.comment}</p>
      </td>

      <td className="hidden sm:table-cell py-3 px-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${platformBadge.className}`}>
          {platformBadge.label}
        </span>
      </td>

      <td className="hidden md:table-cell py-3 px-4">
        <span className="text-gray-500 text-sm">{formatDate(review.original_date)}</span>
      </td>

      <td className="hidden lg:table-cell py-3 px-4">
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            review.is_visible ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
          }`}
        >
          {review.is_visible ? "Visible" : "Masqué"}
        </span>
      </td>
    </TableRow>
  );
}
